import { useTranslation } from "react-i18next";
import { Check, TriangleAlert } from "lucide-react";
import { Dialog, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Disclosure } from "@/components/ui/disclosure";
import { manualCheckPassed, type ManualCheckResult } from "./manual-check.js";

export interface CheckManualDialogProps {
  results: ManualCheckResult[];
  /** What the author asked for — the dialog names it, so "Continue" is never ambiguous about what happens next. */
  action: "submit" | "confirm";
  onContinue: () => void;
  onCancel: () => void;
}

/** The "…anyway" variant is its OWN key per action, never a suffix glued onto the translated label: German puts
 *  "trotzdem" in front, so concatenation produces "Bestätigen anyway". */
const ANYWAY_LABEL = { submit: "manualEditor.submitAnyway", confirm: "manualEditor.confirmAnyway" } as const;

const ACTION_LABEL = { submit: "manualEditor.outlineError.submitForReview", confirm: "manualEditor.outlineError.confirm" } as const;

/**
 * "Check Manual" (ADR-018 addendum §4) — the pre-publish summary the legacy shows before Submit and Confirm.
 *
 * **Always shown**, pass or fail: it is a summary, not an error. The legacy does the same, and a dialog that only
 * appears when something is wrong trains people to dismiss it.
 *
 * **Advisory.** When a row warns the primary becomes "Continue anyway" — still the dialog's main action (owner
 * 2026-10-22: no demotion, no separate warning style; the rows carry the warning). The author decides.
 * The server still enforces its own authz and state CAS; a client check can always be bypassed, so if a HARD rule is
 * ever wanted it has to become a server invariant (§4).
 */
export function CheckManualDialog({ results, action, onContinue, onCancel }: CheckManualDialogProps) {
  const { t } = useTranslation();
  const passed = manualCheckPassed(results);

  return (
    <Dialog isOpen onOpenChange={(open) => !open && onCancel()}>
      <DialogTitle>{t("manualEditor.checkManualTitle")}</DialogTitle>
      <DialogDescription>
        {passed ? t("manualEditor.everythingLooksReady") : t("manualEditor.someThingsAreWorthALook")}
      </DialogDescription>

      <ul className="flex flex-col gap-1 py-1">
        {results.map((r) => (
          <li key={r.key}>
            {r.offenders.length > 0 ? (
              // Only a FAILED row with named offenders is expandable — there is nothing to reveal otherwise. The
              // legacy hides the same list behind a "?" button per row.
              <Disclosure title={<Row result={r} />} className="border-0">
                <ul className="text-muted-foreground flex flex-col gap-0.5 pb-2 pl-6 text-sm">
                  {r.offenders.map((o) => (
                    <li key={o}>{o}</li>
                  ))}
                </ul>
              </Disclosure>
            ) : (
              <div className="px-1">
                <Row result={r} />
              </div>
            )}
          </li>
        ))}
      </ul>

      <DialogFooter>
        {/* Button roles (arc-conventions §3): Abbrechen = ghost; Continue / "… anyway" = the main action. */}
        <Button variant="ghost" onPress={onCancel}>
          {t("manualEditor.cancel")}
        </Button>
        <Button onPress={onContinue}>
          {passed ? t(ACTION_LABEL[action]) : t(ANYWAY_LABEL[action])}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

/** One checklist row: the legacy's ✓/✗ icon plus its sentence. The icon is decorative — the sentence already says
 *  which it is, so a screen reader is not made to read "check" before every line. */
function Row({ result }: { result: ManualCheckResult }) {
  return (
    <span className="flex items-center gap-2 py-1.5 text-left text-sm">
      {result.ok ? (
        <Check aria-hidden="true" className="text-success size-4 shrink-0" />
      ) : (
        <TriangleAlert aria-hidden="true" className="text-attention size-4 shrink-0" />
      )}
      <span>{result.label}</span>
    </span>
  );
}
